<template>
  <Dialog
    :visible="visible"
    modal
    :closable="true"
    :dismissableMask="true"
    :draggable="false"
    :style="{ width: '560px' }"
    :breakpoints="{ '640px': '95vw' }"
    @update:visible="onDialogVisibleChange"
  >
    <!-- Header -->
    <template #header>
      <div class="flex items-center gap-3">
        <span class="material-icons !text-3xl">record_voice_over</span>
        <span>{{ $t('botEditor.connectors.gptlive.name') }}</span>
      </div>
    </template>

    <Divider class="divider-below" />

    <!-- Body -->
    <div class="flex flex-col gap-5">
      <!-- Readiness check in progress -->
      <template v-if="availabilityLoading">
        <Message severity="secondary" variant="simple" size="small" :closable="false">
          {{ $t('common.loading') }}
        </Message>
      </template>

      <!-- GPT-Live not ready: disabled panel with explanatory message -->
      <template v-else-if="!ready">
        <Message severity="warn" :closable="false">
          {{ $t('botEditor.connectors.gptlive.notReady') }}
        </Message>
      </template>

      <!-- Ready: activation status + config form -->
      <template v-else>
        <template v-if="isActive">
          <div class="flex items-center gap-3">
            <Tag :value="$t('botEditor.connectors.gptlive.statusActive')" severity="success" />
          </div>
          <Divider class="divider-flush" />
        </template>

        <div class="flex flex-col gap-2">
          <label for="gptlive-voice" id="gptlive-voice-label" class="font-medium">{{ $t('botEditor.connectors.gptlive.voiceLabel') }}</label>
          <Select
            label-id="gptlive-voice"
            aria-labelledby="gptlive-voice-label"
            v-model="voice"
            :options="voices"
            optionLabel="label"
            optionValue="id"
            class="w-full"
          />
        </div>

        <div class="flex flex-col gap-2">
          <label for="gptlive-environment" id="gptlive-environment-label" class="font-medium">{{ $t('botEditor.connectors.gptlive.environmentLabel') }}</label>
          <Select
            label-id="gptlive-environment"
            aria-labelledby="gptlive-environment-label"
            v-model="botEnvironment"
            :options="environmentOptions"
            optionLabel="label"
            optionValue="value"
            optionDisabled="disabled"
            class="w-full"
          />
          <small v-if="!productionBotId" class="text-gray-500">
            {{ $t('botEditor.connectors.gptlive.environmentNotDeployed') }}
          </small>
        </div>

        <Divider class="divider-flush" />

        <div class="flex flex-col gap-2">
          <label for="gptlive-manager-prompt" class="font-medium">{{ $t('botEditor.connectors.gptlive.managerPromptLabel') }}</label>
          <Textarea
            id="gptlive-manager-prompt"
            v-model="managerPrompt"
            rows="4"
            autoResize
            class="w-full"
          />
          <small class="text-gray-500">{{ $t('botEditor.connectors.gptlive.managerPromptHelp') }}</small>
        </div>

        <div class="flex flex-col gap-2">
          <label for="gptlive-channel-prompt" class="font-medium">{{ $t('botEditor.connectors.gptlive.channelPromptLabel') }}</label>
          <Textarea
            id="gptlive-channel-prompt"
            v-model="channelPrompt"
            rows="6"
            autoResize
            class="w-full"
          />
          <small v-if="!channelPromptInvalid" class="text-gray-500">{{ $t('botEditor.connectors.gptlive.channelPromptHelp') }}</small>
          <Message v-else severity="error" variant="simple" size="small" :closable="false">
            {{ $t('botEditor.connectors.gptlive.channelPromptTooShort') }}
          </Message>
        </div>

        <!-- Apertura della chiamata: benvenuto oppure flusso, sempre una delle due -->
        <Divider class="divider-flush" />
        <div class="flex flex-col gap-2">
          <span id="gptlive-opening-label" class="font-medium">{{ $t('botEditor.connectors.gptlive.openingBehaviorLabel') }}</span>
          <div role="radiogroup" aria-labelledby="gptlive-opening-label" class="flex flex-wrap gap-x-6 gap-y-2">
            <div v-for="option in openingBehaviorOptions" :key="option.value" class="flex items-center gap-2">
              <RadioButton v-model="openingBehavior" :inputId="`gptlive-opening-${option.value}`" :value="option.value" />
              <label :for="`gptlive-opening-${option.value}`" class="cursor-pointer">{{ option.label }}</label>
            </div>
          </div>

          <div v-if="openingBehavior === 'welcome'" class="flex flex-col gap-2 mt-1">
            <Textarea
              v-model="welcomeMessage"
              :aria-label="$t('botEditor.connectors.gptlive.openingWelcome')"
              :placeholder="$t('botEditor.connectors.configModal.welcomeMessagePlaceholder')"
              :aria-describedby="openingIsMissing ? 'gptlive-opening-missing' : undefined"
              rows="3"
              autoResize
              class="w-full"
            />
            <small v-if="openingIsMissing" id="gptlive-opening-missing" class="text-gray-400">{{ $t('botEditor.connectors.gptlive.openingWelcomeRequired') }}</small>
          </div>
          <div v-else-if="openingBehavior === 'flow'" class="flex flex-col gap-2 mt-1">
            <Select
              v-model="initialFlowId"
              :aria-label="$t('botEditor.connectors.gptlive.openingFlow')"
              :options="flowOptions"
              optionLabel="label"
              optionValue="value"
              :disabled="flows.length === 0"
              :aria-describedby="openingIsMissing && flows.length > 0 ? 'gptlive-opening-missing' : undefined"
              class="w-full"
            />
            <small v-if="openingIsMissing && flows.length > 0" id="gptlive-opening-missing" class="text-gray-400">{{ $t('botEditor.connectors.gptlive.openingFlowRequired') }}</small>
            <Message v-if="flows.length === 0" severity="warn" :closable="false">
              {{ $t('botEditor.connectors.initialFlowHint') }}
            </Message>
          </div>
        </div>

        <!-- Chiamata di prova WebRTC con didascalie live -->
        <Divider class="divider-flush" />
        <div class="flex flex-col gap-2">
          <VoiceCostNotice
            v-if="pricePerMinute !== null"
            :per-minute-eur="pricePerMinute"
            message-key="botEditor.connectors.gptlive.costNotice"
          />
          <div class="flex items-center gap-3">
            <Button
              v-if="callState === 'idle'"
              severity="secondary"
              outlined
              :disabled="!canTest"
              @click="startTest"
            >
              <span class="material-icons">mic</span>
              {{ $t('botEditor.connectors.gptlive.test') }}
            </Button>
            <Button
              v-else
              severity="danger"
              outlined
              @click="stopTest"
            >
              <!-- Niente :loading: PrimeVue disabilita il Button mentre carica, e una
                   chiamata bloccata in connessione (microfono aperto, sessione
                   OpenAI a pagamento) deve restare interrompibile. Lo spinner è un
                   ProgressSpinner decorativo nello slot (lo stato è già nel Message
                   accanto); `margin: 0` annulla il suo `margin: 0 auto`. -->
              <ProgressSpinner
                v-if="callState === 'connecting'"
                style="width: 1.25rem; height: 1.25rem; margin: 0"
                stroke-width="4"
                aria-hidden="true"
              />
              <span v-else class="material-icons">call_end</span>
              {{ $t('botEditor.connectors.gptlive.endTest') }}
            </Button>

            <Message v-if="callState === 'connecting'" severity="secondary" variant="simple" size="small" :closable="false">
              {{ $t('botEditor.connectors.gptlive.testConnecting') }}
            </Message>
            <Message v-else-if="callState === 'active'" severity="success" variant="simple" size="small" :closable="false">
              {{ $t('botEditor.connectors.gptlive.testActive') }}
            </Message>
          </div>

          <Message v-if="callState === 'idle' && !canTest" severity="secondary" variant="simple" size="small" :closable="false">
            {{ $t('botEditor.connectors.gptlive.testNeedsActivation') }}
          </Message>

          <div v-if="captions.length" class="mt-2 rounded-md border border-gray-700 bg-gray-800/60">
            <p id="gptlive-transcript-label" class="px-3 pt-2 pb-1 text-xs font-medium text-gray-400 border-b border-gray-700/70">
              {{ $t('botEditor.connectors.gptlive.transcriptLabel') }}
            </p>
            <!-- role="log": i lettori di schermo annunciano le didascalie man mano
                 che arrivano. Lo scroll segue la conversazione finché l'utente
                 non risale a rileggere (vedi followCaptions). -->
            <div
              ref="captionsScroller"
              role="log"
              aria-live="polite"
              aria-labelledby="gptlive-transcript-label"
              tabindex="0"
              class="flex flex-col gap-1 max-h-40 sm:max-h-56 overflow-y-auto p-3 focus-visible:outline-2 focus-visible:outline-cyan-400"
              @scroll="onCaptionsScroll"
            >
              <p v-for="(entry, idx) in captions" :key="idx" class="text-sm text-gray-300 break-words">
                <span class="font-semibold">{{ entry.role === 'user' ? $t('botEditor.connectors.gptlive.callerLabel') : $t('botEditor.connectors.gptlive.assistantLabel') }}:</span>
                {{ entry.text }}
              </p>
            </div>
          </div>
        </div>

        <!-- Telefonia Twilio: visibile solo a connettore attivo -->
        <template v-if="isActive">
          <Divider class="divider-flush" />
          <section class="flex flex-col gap-4" aria-labelledby="gptlive-telephony-title">
            <div class="flex flex-col gap-1">
              <h4 id="gptlive-telephony-title" class="text-lg font-semibold">{{ $t('botEditor.connectors.gptlive.telephony.section') }}</h4>
              <small class="text-gray-400">{{ $t('botEditor.connectors.gptlive.telephony.help') }}</small>
            </div>

            <div v-if="linked" class="flex items-center gap-3">
              <Tag :value="$t('botEditor.connectors.gptlive.telephony.linked')" severity="success" />
              <span class="text-sm text-gray-300 font-mono">{{ linked.number }}</span>
            </div>

            <div class="flex flex-col gap-2">
              <label for="gptlive-tel-sid" class="font-medium">{{ $t('botEditor.connectors.gptlive.telephony.accountSidLabel') }}</label>
              <InputText id="gptlive-tel-sid" v-model="telAccountSid" :placeholder="$t('botEditor.connectors.gptlive.telephony.accountSidPlaceholder')"
                :invalid="!!telErrors.accountSid" :aria-describedby="telErrors.accountSid ? 'gptlive-tel-sid-err' : undefined" autocomplete="off" class="w-full" />
              <small v-if="telErrors.accountSid" id="gptlive-tel-sid-err" class="text-red-400">{{ $t(`botEditor.connectors.gptlive.telephony.errors.${telErrors.accountSid}`) }}</small>
            </div>

            <div class="flex flex-col gap-2">
              <label for="gptlive-tel-token" class="font-medium">{{ $t('botEditor.connectors.gptlive.telephony.authTokenLabel') }}</label>
              <Password inputId="gptlive-tel-token" v-model="telAuthToken" :feedback="false" toggleMask autocomplete="new-password"
                :placeholder="linked?.hasToken ? $t('botEditor.connectors.gptlive.telephony.secretSet') : $t('botEditor.connectors.gptlive.telephony.authTokenPlaceholder')"
                :invalid="!!telErrors.authToken" :aria-describedby="telErrors.authToken ? 'gptlive-tel-token-err' : undefined" class="w-full" inputClass="w-full" />
              <small v-if="telErrors.authToken" id="gptlive-tel-token-err" class="text-red-400">{{ $t(`botEditor.connectors.gptlive.telephony.errors.${telErrors.authToken}`) }}</small>
            </div>

            <div class="flex flex-col gap-2">
              <label for="gptlive-tel-number" class="font-medium">{{ $t('botEditor.connectors.gptlive.telephony.numberLabel') }}</label>
              <InputText id="gptlive-tel-number" v-model="telNumber" :placeholder="$t('botEditor.connectors.gptlive.telephony.numberPlaceholder')"
                :invalid="!!telErrors.number" :aria-describedby="telErrors.number ? 'gptlive-tel-number-err' : undefined" inputmode="tel" autocomplete="off" class="w-full" />
              <small v-if="telErrors.number" id="gptlive-tel-number-err" class="text-red-400">{{ $t(`botEditor.connectors.gptlive.telephony.errors.${telErrors.number}`) }}</small>
            </div>

            <div class="flex gap-2">
              <Button severity="primary" :label="$t('botEditor.connectors.gptlive.telephony.link')" :loading="telLoading" :disabled="telLoading" @click="linkTelephony">
                <template #icon>
                  <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">phone</span>
                </template>
              </Button>
              <Button v-if="linked" severity="danger" outlined :label="$t('botEditor.connectors.gptlive.telephony.unlink')" :loading="telLoading" :disabled="telLoading" @click="unlinkTelephony">
                <template #icon>
                  <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">phone_disabled</span>
                </template>
              </Button>
            </div>
          </section>
        </template>
      </template>
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="flex items-center justify-between gap-2">
        <div>
          <Button
            v-if="ready && isActive"
            severity="danger"
            outlined
            :label="$t('botEditor.connectors.gptlive.deactivate')"
            :loading="actionLoading"
            :disabled="actionLoading"
            @click="deactivate"
          >
            <template #icon>
              <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">mic_off</span>
            </template>
          </Button>
        </div>
        <div class="flex gap-2">
          <Button
            :label="$t('botEditor.connectors.configModal.cancelButton')"
            severity="secondary"
            @click="closeDialog"
          />
          <Button
            v-if="ready"
            severity="primary"
            :label="$t(isActive ? 'botEditor.connectors.gptlive.save' : 'botEditor.connectors.gptlive.activate')"
            :loading="actionLoading"
            :disabled="actionLoading || channelPromptInvalid || openingIsMissing || !voice"
            @click="saveOrActivate"
          >
            <template #icon>
              <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">{{ isActive ? 'save' : 'mic' }}</span>
            </template>
          </Button>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import api from '../../services/api';
import { CHANNEL_PROMPT_DEFAULTS } from '../../constants/channelPromptDefaults';
import { startLiveCall } from '../../utils/gptLiveWebrtc';
import { reduceCaption } from '../../utils/gptLiveTranscript';
import { initialOpening, openingMissing } from '../../utils/gptLiveOpening';
import { linkedTelephony, validateTelephonyForm, buildTelephonyBody, telephonyErrorKey, unlinkWarningKey } from '../../utils/gptLiveTelephony';
import VoiceCostNotice from '../voice/VoiceCostNotice.vue';

const props = defineProps({
  visible: { type: Boolean, default: false },
  workspaceId: { type: String, required: true },
  botId: { type: String, required: true },
  connector: { type: Object, default: null },
  flows: { type: Array, default: () => [] },
  // Senza un bot di produzione pubblicato l'ambiente 'production' non ha
  // niente a cui rispondere: come nel modal WhatsApp, l'opzione è disabilitata.
  productionBotId: { type: String, default: null },
});
const emit = defineEmits(['update:visible', 'saved']);

const { t } = useI18n();
const toast = useToast();

const availabilityLoading = ref(false);
const ready = ref(null); // null (sconosciuto) | true | false
const voices = ref([]);  // [{ id, label, language }] da availability
const pricePerMinute = ref(null); // €/min del profilo, da availability
const voice = ref(null);
const botEnvironment = ref('preview');
const managerPrompt = ref('');
const channelPrompt = ref('');
const openingBehavior = ref('welcome'); // 'welcome' | 'flow'
const welcomeMessage = ref('');
const initialFlowId = ref(null);
const actionLoading = ref(false);

const telAccountSid = ref('');
const telAuthToken = ref('');
const telNumber = ref('');
const telErrors = ref({});
const telLoading = ref(false);
const linked = computed(() => linkedTelephony(props.connector));

const callState = ref('idle'); // 'idle' | 'connecting' | 'active'
const captions = ref([]);
let activeCall = null; // { stop, events } — non reattivo, non serve nel template
// Token del tentativo di chiamata in corso: stopTest() lo incrementa per
// invalidare un startTest() ancora in attesa di startLiveCall (dialog chiuso,
// componente smontato o "Termina" premuto mentre lo stato è 'connecting',
// quando activeCall è ancora null e quindi stopTest() non avrebbe nulla da fermare).
let callAttempt = 0;

const isActive = computed(() => props.connector?.enabled === true);
const canTest = computed(() => isActive.value);

const openingIsMissing = computed(() => openingMissing(openingBehavior.value, welcomeMessage.value, initialFlowId.value));

const channelPromptInvalid = computed(() => {
  return !channelPrompt.value || channelPrompt.value.trim().length < 10;
});

const environmentOptions = computed(() => [
  { label: t('botEditor.connectors.gptlive.environmentPreview'), value: 'preview' },
  { label: t('botEditor.connectors.gptlive.environmentProduction'), value: 'production', disabled: !props.productionBotId },
]);

const openingBehaviorOptions = computed(() => [
  { label: t('botEditor.connectors.gptlive.openingWelcome'), value: 'welcome' },
  { label: t('botEditor.connectors.gptlive.openingFlow'), value: 'flow' },
]);

// Codici d'errore noti di /gptlive/activate e /live/.../test-session: il
// message del backend non è tradotto (e a volte è in inglese), quindi nel
// toast va la chiave i18n; un codice sconosciuto non aggiunge dettaglio.
const ERROR_DETAIL_KEYS = {
  GPTLIVE_NOT_READY: 'botEditor.connectors.gptlive.notReady',
  INVALID_VOICE: 'botEditor.connectors.gptlive.errors.invalidVoice',
  INVALID_ENVIRONMENT: 'botEditor.connectors.gptlive.errors.invalidEnvironment',
  INVALID_CHANNEL_PROMPT: 'botEditor.connectors.gptlive.errors.invalidChannelPrompt',
  INVALID_OPENING: 'botEditor.connectors.gptlive.errors.invalidOpening',
  OPENING_REQUIRED: 'botEditor.connectors.gptlive.errors.openingRequired',
  CONNECTOR_NOT_ACTIVE: 'botEditor.connectors.gptlive.errors.connectorNotActive',
  LIVE_SESSION_FAILED: 'botEditor.connectors.gptlive.errors.liveSessionFailed',
  SDP_REQUIRED: 'botEditor.connectors.gptlive.errors.sdpRequired',
  RATE_LIMITED: 'botEditor.connectors.gptlive.errors.rateLimited',
  TOO_MANY_SESSIONS: 'botEditor.connectors.gptlive.errors.tooManySessions',
  CREDIT_EXHAUSTED: 'botEditor.connectors.gptlive.errors.creditExhausted',
  SERVER_DRAINING: 'botEditor.connectors.gptlive.errors.serverDraining',
  CONNECTOR_UNAVAILABLE: 'botEditor.connectors.unavailableError',
};

function errorDetail(error) {
  const key = ERROR_DETAIL_KEYS[error?.response?.data?.error?.code];
  return key ? t(key) : undefined;
}

const flowOptions = computed(() => props.flows.map((flow) => ({ label: flow.name, value: flow._id })));

function normalizeFlowId(rawFlowId) {
  if (rawFlowId && typeof rawFlowId === 'object') {
    return rawFlowId._id || rawFlowId.toString?.() || null;
  }
  return rawFlowId || null;
}

async function openDialog() {
  ready.value = null;
  availabilityLoading.value = true;
  voices.value = [];
  pricePerMinute.value = null;
  captions.value = [];
  callState.value = 'idle';

  voice.value = props.connector?.config?.voice || null;
  botEnvironment.value = props.connector?.config?.botEnvironment || 'preview';
  managerPrompt.value = props.connector?.config?.managerPrompt || '';
  channelPrompt.value = props.connector?.channelPrompt || CHANNEL_PROMPT_DEFAULTS.gptlive;
  welcomeMessage.value = props.connector?.conversationConfig?.welcomeMessage || '';
  initialFlowId.value = normalizeFlowId(props.connector?.conversationConfig?.initialFlowId);
  openingBehavior.value = initialOpening({ initialFlowId: initialFlowId.value });

  telAccountSid.value = linked.value?.accountSid || '';
  telNumber.value = linked.value?.number || '';
  telAuthToken.value = '';
  telErrors.value = {};

  try {
    const response = await api.get(`/workspaces/${props.workspaceId}/bots/${props.botId}/gptlive/availability`);
    ready.value = !!response.data?.data?.ready;
    voices.value = response.data?.data?.voices || [];
    pricePerMinute.value = Number(response.data?.data?.pricePerMinuteEur) || 0;
    if (!voice.value && voices.value.length) voice.value = voices.value[0].id;
  } catch (error) {
    Console.error('[GptLiveConnectorDialog] Error checking availability:', error);
    ready.value = false;
  } finally {
    availabilityLoading.value = false;
  }
}

watch(() => props.visible, (isVisible) => {
  if (isVisible) openDialog();
});

// Il Dialog emette update:visible solo per chiudersi (X, ESC, click sul mask):
// non c'è un percorso in cui lo emette con true.
function onDialogVisibleChange() {
  closeDialog();
}

function closeDialog() {
  stopTest();
  captions.value = [];
  emit('update:visible', false);
}

async function saveOrActivate() {
  // Letto prima della richiesta: dopo 'saved' il padre ricarica il connettore
  // e isActive diventa true anche per una prima attivazione.
  const wasActive = isActive.value;
  actionLoading.value = true;
  try {
    await api.post(`/workspaces/${props.workspaceId}/bots/${props.botId}/gptlive/activate`, {
      voice: voice.value,
      botEnvironment: botEnvironment.value,
      managerPrompt: managerPrompt.value,
      channelPrompt: channelPrompt.value,
      conversationConfig: {
        welcomeMessage: openingBehavior.value === 'welcome' ? welcomeMessage.value : '',
        initialFlowId: openingBehavior.value === 'flow' ? initialFlowId.value : null,
      },
    });
    toast.add({
      severity: 'success',
      summary: t(wasActive ? 'botEditor.connectors.toastSaved' : 'botEditor.connectors.toastActivated'),
      life: 3000,
    });
    emit('saved');
  } catch (error) {
    Console.error('[GptLiveConnectorDialog] Error saving connector:', error);
    const code = error.response?.data?.error?.code;
    if (code === 'GPTLIVE_NOT_READY') ready.value = false;
    toast.add({
      severity: 'error',
      summary: t('botEditor.connectors.toastSaveError'),
      detail: errorDetail(error),
      life: 5000,
    });
  } finally {
    actionLoading.value = false;
  }
}

async function deactivate() {
  actionLoading.value = true;
  try {
    await api.post(`/workspaces/${props.workspaceId}/bots/${props.botId}/gptlive/deactivate`);
    toast.add({ severity: 'success', summary: t('botEditor.connectors.toastDeactivated'), life: 3000 });
    emit('saved');
  } catch (error) {
    Console.error('[GptLiveConnectorDialog] Error deactivating connector:', error);
    toast.add({ severity: 'error', summary: t('botEditor.connectors.toastError'), life: 5000 });
  } finally {
    actionLoading.value = false;
  }
}

async function linkTelephony() {
  const check = validateTelephonyForm({ accountSid: telAccountSid.value, authToken: telAuthToken.value, number: telNumber.value, linked: linked.value });
  telErrors.value = check.errors;
  if (!check.valid) return;
  telLoading.value = true;
  try {
    await api.post(`/workspaces/${props.workspaceId}/bots/${props.botId}/gptlive/telephony`,
      buildTelephonyBody({ accountSid: telAccountSid.value, authToken: telAuthToken.value, number: telNumber.value }));
    telAuthToken.value = '';
    toast.add({ severity: 'success', summary: t('botEditor.connectors.gptlive.telephony.linkedToast'), life: 3000 });
    emit('saved');
  } catch (error) {
    Console.error('[GptLiveConnectorDialog] Error linking telephony:', error);
    toast.add({ severity: 'error', summary: t('botEditor.connectors.gptlive.telephony.error'), detail: t(telephonyErrorKey(error?.response?.data?.error?.code)), life: 6000 });
  } finally {
    telLoading.value = false;
  }
}

async function unlinkTelephony() {
  telLoading.value = true;
  try {
    const response = await api.post(`/workspaces/${props.workspaceId}/bots/${props.botId}/gptlive/telephony/remove`);
    telNumber.value = '';
    telAuthToken.value = '';
    const warnings = response.data?.data?.warnings;
    const warningKey = unlinkWarningKey(warnings);
    if (warningKey) {
      // I codici grezzi (number_gone, decrypt) restano nel log: all'utente un avviso leggibile.
      Console.warn('[GptLiveConnectorDialog] Telephony unlink warnings:', warnings);
      toast.add({ severity: 'warn', summary: t(warningKey), life: 8000 });
    } else {
      toast.add({ severity: 'success', summary: t('botEditor.connectors.gptlive.telephony.unlinkedToast'), life: 3000 });
    }
    emit('saved');
  } catch (error) {
    // 502 TELEPHONY_DETACH_FAILED (warnings ['restore_voice']): Twilio non ha
    // ripristinato la configurazione voce del numero. Il backend lascia il
    // collegamento com'è così l'utente può ritentare — niente toast di
    // successo, niente svuotamento dei campi.
    Console.error('[GptLiveConnectorDialog] Error unlinking telephony:', error);
    const warnings = error?.response?.data?.data?.warnings;
    if (warnings?.length) Console.warn('[GptLiveConnectorDialog] Telephony detach warnings:', warnings);
    toast.add({ severity: 'error', summary: t('botEditor.connectors.gptlive.telephony.error'), detail: t(telephonyErrorKey(error?.response?.data?.error?.code)), life: 6000 });
  } finally {
    telLoading.value = false;
  }
}

async function startTest() {
  if (!canTest.value || callState.value !== 'idle') return;
  captions.value = [];
  callState.value = 'connecting';
  const myAttempt = ++callAttempt;

  let call;
  try {
    call = await startLiveCall({
      getAnswer: async (sdp) => {
        const response = await api.post(`/live/workspaces/${props.workspaceId}/bots/${props.botId}/test-session`, { sdp });
        return response.data.data.sdp;
      },
    });
  } catch (error) {
    if (myAttempt !== callAttempt) return; // già abortito (dialog chiuso/Termina): ignora
    Console.error('[GptLiveConnectorDialog] Error starting test call:', error);
    if (error?.name === 'NotAllowedError') {
      toast.add({ severity: 'warn', summary: t('botEditor.connectors.gptlive.micDenied'), life: 5000 });
    } else {
      toast.add({ severity: 'error', summary: t('botEditor.connectors.gptlive.testError'), detail: errorDetail(error), life: 5000 });
    }
    callState.value = 'idle';
    return;
  }

  if (myAttempt !== callAttempt) {
    // stopTest() è stato chiamato mentre eravamo ancora in attesa di
    // startLiveCall: activeCall era null, quindi non aveva nulla da fermare.
    // La chiamata è comunque partita nel frattempo: chiuderla subito, senza
    // agganciare listener (nessuno la ascolterebbe più).
    try { call.stop(); } catch (error) { /* già chiusa */ }
    return;
  }

  activeCall = call;
  callState.value = 'active';
  call.events.addEventListener('event', (e) => {
    const detail = e.detail;
    captions.value = reduceCaption(captions.value, detail);
    // Ridondante da quando si passa ad 'active' già alla risoluzione di
    // startLiveCall, ma innocuo: lasciarlo non cambia nulla di osservabile.
    if (detail?.type === 'session.started') callState.value = 'active';
  });
  call.events.addEventListener('ended', () => {
    // 'ended' può arrivare a chiusura ordinata finita, quando questa chiamata
    // è già stata fermata e magari ne è partita un'altra: fermare solo se è
    // ancora quella attiva, altrimenti si chiuderebbe la nuova.
    if (activeCall === call) stopTest();
  });
}

// Didascalie: lo scroll resta agganciato al fondo finché l'utente non risale
// a rileggere; tornato in fondo, riprende a seguire.
const captionsScroller = ref(null);
let followCaptions = true;

function onCaptionsScroll() {
  const el = captionsScroller.value;
  if (!el) return;
  followCaptions = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
}

watch(captions, async (next, prev) => {
  if (!next.length) { followCaptions = true; return; }
  if (!followCaptions && prev?.length) return;
  await nextTick();
  const el = captionsScroller.value;
  if (el) el.scrollTop = el.scrollHeight;
});

function stopTest() {
  callAttempt++; // invalida un eventuale startTest() ancora in corso
  if (activeCall) {
    // stop() manda session.close e rilascia media e sessione solo a
    // session.closed (o al timeout): qui non serve aspettarlo.
    try { activeCall.stop(); } catch (error) { /* già chiusa */ }
    activeCall = null;
  }
  callState.value = 'idle';
}

onBeforeUnmount(() => {
  stopTest();
});
</script>
